import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Table, Button, Space, Tag, message, Input, Card, Popconfirm,
  Tooltip, Spin, Switch, Modal, Form, Select, Progress, Typography
} from 'antd';
import type { TableProps } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { glossaryService, GlossaryEntry, CreateGlossaryPayload } from '../services/glossaryService';

const { Text: TypographyText } = Typography;

const MAX_ENTRIES = 200; // Token budget cap (~3,000-4,000 tokens at 15-20 tokens/entry)

export const GlossaryManagement: React.FC = () => {
  const [entries, setEntries] = useState<GlossaryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [showInactive, setShowInactive] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<GlossaryEntry | null>(null);
  const [form] = Form.useForm();
  const [saveLoading, setSaveLoading] = useState(false);

  // Derive categories and count from loaded entries (no extra API calls)
  const categories = useMemo(
    () => [...new Set(entries.map(e => e.category))].sort(),
    [entries]
  );

  const activeCount = useMemo(
    () => entries.filter(e => e.is_active).length,
    [entries]
  );

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await glossaryService.getAllEntries(showInactive);
      setEntries(data);
    } catch (error: any) {
      message.error(`Failed to load glossary: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchEntries();
  }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──

  const handleCreate = async (values: any) => {
    setSaveLoading(true);
    try {
      const payload: CreateGlossaryPayload = {
        term: values.term,
        canonical: values.canonical,
        category: values.category_new?.trim() || values.category_existing,
        description: values.description,
        created_by: 'admin',
      };
      await glossaryService.createEntry(payload);
      message.success(`Entry "${payload.term}" created`);
      setShowModal(false);
      form.resetFields();
      fetchEntries();
    } catch (error: any) {
      message.error(`Failed to create entry: ${error.message}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdate = async (values: any) => {
    if (!editingEntry) return;
    setSaveLoading(true);
    try {
      await glossaryService.updateEntry(editingEntry.id, editingEntry.category, {
        term: values.term,
        canonical: values.canonical,
        description: values.description,
        updated_by: 'admin',
      });
      message.success('Entry updated');
      setShowModal(false);
      setEditingEntry(null);
      form.resetFields();
      fetchEntries();
    } catch (error: any) {
      message.error(`Failed to update: ${error.message}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (entry: GlossaryEntry) => {
    setActionLoading(entry.id);
    try {
      await glossaryService.deleteEntry(entry.id, entry.category);
      message.success(`Entry "${entry.term}" deleted`);
      fetchEntries();
    } catch (error: any) {
      message.error(`Failed to delete: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggle = async (entry: GlossaryEntry) => {
    setActionLoading(entry.id);
    try {
      const updated = await glossaryService.toggleEntry(entry.id, entry.category);
      message.success(`Entry "${entry.term}" ${updated.is_active ? 'activated' : 'deactivated'}`);
      fetchEntries();
    } catch (error: any) {
      message.error(`Failed to toggle: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const openCreateModal = () => {
    setEditingEntry(null);
    form.resetFields();
    setShowModal(true);
  };

  const openEditModal = (entry: GlossaryEntry) => {
    setEditingEntry(entry);
    form.setFieldsValue({
      term: entry.term,
      canonical: entry.canonical,
      category_existing: entry.category,
      description: entry.description || '',
    });
    setShowModal(true);
  };

  // ── Filtering ──

  const filteredData = entries.filter((item) => {
    const matchesSearch =
      item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.canonical.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // ── Table columns ──

  const columns: TableProps<GlossaryEntry>['columns'] = [
    {
      title: 'Citizen Term',
      dataIndex: 'term',
      key: 'term',
      width: 200,
      sorter: (a, b) => a.term.localeCompare(b.term),
      render: (text: string) => (
        <span style={{ fontWeight: 600 }}>{text}</span>
      ),
    },
    {
      title: 'Canonical Meaning',
      dataIndex: 'canonical',
      key: 'canonical',
      width: 260,
      render: (text: string) => (
        <span>{text}</span>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (cat: string) => (
        <Tag color="blue">{cat}</Tag>
      ),
      filters: categories.map(c => ({ text: c, value: c })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Status',
      key: 'status',
      width: 90,
      align: 'center' as const,
      render: (_: any, record: GlossaryEntry) => (
        <Switch
          checked={record.is_active}
          loading={actionLoading === record.id}
          onChange={() => handleToggle(record)}
          size="small"
          checkedChildren="On"
          unCheckedChildren="Off"
        />
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200,
      render: (text: string) =>
        text ? (
          <Tooltip title={text}>
            <span>{text}</span>
          </Tooltip>
        ) : (
          <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>—</span>
        ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 130,
      sorter: (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
      render: (date: string) => (
        <div style={{ fontSize: 12 }}>
          <div>{new Date(date).toLocaleDateString()}</div>
          <div style={{ color: '#8c8c8c' }}>{new Date(date).toLocaleTimeString()}</div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: any, record: GlossaryEntry) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            >
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete this entry?"
            description={`"${record.term}" will be permanently removed.`}
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={actionLoading === record.id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ──

  if (loading && entries.length === 0) {
    return (
      <div className="admin-content-container">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  const remaining = MAX_ENTRIES - activeCount;
  const usagePercent = Math.round((activeCount / MAX_ENTRIES) * 100);

  return (
    <div className="admin-content-container">
      {/* Header */}
      <div className="page-header-row">
        <h2 className="page-title">Domain Glossary</h2>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            size="large"
            onClick={fetchEntries}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            disabled={remaining <= 0}
          >
            Add Entry
          </Button>
        </Space>
      </div>

      {/* Entry count indicator */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <Progress
              percent={usagePercent}
              size="small"
              status={usagePercent > 90 ? 'exception' : 'normal'}
              format={() => `${activeCount} / ${MAX_ENTRIES}`}
            />
          </div>
          <TypographyText type="secondary" style={{ fontSize: 12 }}>
            {remaining} entries remaining (~{remaining * 18} tokens)
          </TypographyText>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>Show inactive:</span>
            <Switch
              size="small"
              checked={showInactive}
              onChange={setShowInactive}
            />
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="Search terms or meanings..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            style={{ width: 280 }}
          />
          <Select
            placeholder="All categories"
            allowClear
            style={{ width: 180 }}
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categories.map(c => ({ label: c, value: c }))}
          />
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} entr${total !== 1 ? 'ies' : 'y'}`,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          size="middle"
          scroll={{ x: 1100 }}
          rowClassName={(record) => (record.is_active ? '' : 'disabled-prompt-row')}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingEntry ? `Edit: ${editingEntry.term}` : 'Add Glossary Entry'}
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          setEditingEntry(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          onFinish={editingEntry ? handleUpdate : handleCreate}
          layout="vertical"
        >
          <Form.Item
            name="term"
            label="Citizen Term"
            rules={[
              { required: true, message: 'Enter the citizen shorthand' },
              { min: 2, message: 'Term must be at least 2 characters' },
            ]}
            tooltip="The colloquial/short term citizens use (e.g. 'the dole')"
          >
            <Input placeholder="e.g. the dole" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="canonical"
            label="Canonical Meaning"
            rules={[
              { required: true, message: 'Enter the official term' },
              { min: 2, message: 'Meaning must be at least 2 characters' },
            ]}
            tooltip="The official/canonical term (e.g. 'Jobseeker's Allowance')"
          >
            <Input placeholder="e.g. Jobseeker's Allowance" maxLength={200} />
          </Form.Item>

          {!editingEntry && (
            <>
              <Form.Item
                name="category_existing"
                label="Category"
                tooltip="Select an existing category or create a new one below"
              >
                <Select
                  placeholder="Select existing category"
                  allowClear
                  options={categories.map(c => ({ label: c, value: c }))}
                />
              </Form.Item>
              <Form.Item
                name="category_new"
                label="Or New Category"
                rules={[
                  {
                    validator: (_, value) => {
                      const existing = form.getFieldValue('category_existing');
                      if (!existing && !value?.trim()) {
                        return Promise.reject('Select a category or enter a new one');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="e.g. social_welfare" maxLength={50} />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="description"
            label="Description (optional)"
            tooltip="Admin-facing notes about this entry"
          >
            <Input.TextArea placeholder="Additional context..." rows={2} maxLength={300} showCount />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setShowModal(false); setEditingEntry(null); form.resetFields(); }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={saveLoading}>
                {editingEntry ? 'Save Changes' : 'Add Entry'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
